import React from 'react';
import {Box, Typography} from '@mui/material';
import {OVPot} from '../../../schema-types/OVPot';
import {
  OVBuilders_ovrecommends,
  OVBuilders_ovrecommends_recommends,
} from '../../../schema-types/OVBuilders';

const modifyChangesString: Record<string, string> = {
  'Change in fee': 'fee',
  'Change in mandate': 'mandate',
  'Change in regular contribution': 'regular contribution',
  'Change in regular withdrawal': 'regular withdrawal',
  'Change in ownership': 'ownership',
};
const getProvider = (pot: Pick<OVPot, 'pot'>) =>
  (pot.pot.productProvider?.reportName ?? pot.pot.productProvider?.name ?? '').split(
    ' '
  )[0];

const getProduct = (pot: Pick<OVPot, 'pot'>) =>
  pot.pot.productName?.reportName?.split('-')[0] ?? pot.pot.productName?.formattedName;

const provProd = (pot: Pick<OVPot, 'pot'>) => `${getProvider(pot)} ${getProduct(pot)}`;

const getPotFromBuilder = (
  rowOvr: Pick<OVBuilders_ovrecommends, 'pots'>,
  rec: Pick<OVBuilders_ovrecommends_recommends, 'id'>
) => rowOvr.pots?.find((p) => p.recommendations.some((r) => r.id === rec.id));

const getBuilderFromPot = (
  rowOvr: Pick<OVBuilders_ovrecommends, 'recommends'>,
  pot: Pick<OVPot, 'recommendations'>
) => rowOvr.recommends?.find((x) => pot?.recommendations.some((y) => y.id === x.id));

const newOrExisting = (
  rec: Pick<OVBuilders_ovrecommends_recommends, 'advice'> | undefined
) => {
  if (!rec) {
    return '';
  }
  return rec.advice?.toLocaleLowerCase().includes('setup a new') ? 'new ' : 'existing';
};

const isSingle = (rec: Pick<OVBuilders_ovrecommends_recommends, 'advice'>) =>
  rec.advice?.toLocaleLowerCase().includes('monthly') ||
  rec.advice?.toLocaleLowerCase().includes('quarterly') ||
  rec.advice?.toLocaleLowerCase().includes('annual')
    ? 'regular'
    : 'lump sum';

const MAX_NOTE_LENGTH = 80;
const noteSummary = (note: string) =>
  note.length < MAX_NOTE_LENGTH ? note : `${note.slice(0, MAX_NOTE_LENGTH - 3)}...`;

export type ComprehensiveBuilderSummaryRow = OVBuilders_ovrecommends | null | undefined;

export const getComprehensiveBuilderSummary = (
  rowOvr: Pick<OVBuilders_ovrecommends, 'recommends' | 'contact' | 'pots' | 'note'>
) => {
  if (!rowOvr.recommends) {
    return [];
  }
  let result: string[] = [];

  const owner = rowOvr.contact?.simplifiedName;
  const sourcePots = rowOvr.pots?.filter((pot) => pot.planType === 'source') ?? [];
  let sourcePotsIndex = 0;

  result = rowOvr.recommends?.map((rec) => {
    const lowerCaseAdvice = rec.advice?.toLocaleLowerCase() ?? '';
    const pot = getPotFromBuilder(rowOvr, rec);

    if (!pot) {
      return rec.advice ?? '';
    }

    if (lowerCaseAdvice.includes('setup a new')) {
      const subOrNew = pot.pot.parent ? 'sub' : 'new';
      return `${owner} - Open a ${subOrNew} ${provProd(pot)}`;
    }

    if (lowerCaseAdvice.includes('transfer')) {
      const partial = lowerCaseAdvice.includes('partial') ? 'Partial ' : '';

      const resultLine = `${partial}Transfer ${owner}'s ${provProd(
        sourcePots[sourcePotsIndex]
      )} to their ${newOrExisting(getBuilderFromPot(rowOvr, pot))} ${provProd(pot)}`;
      sourcePotsIndex += 1;
      return resultLine;
    }

    if (lowerCaseAdvice.includes('contribution')) {
      return `Make a ${isSingle(rec)} contribution to ${owner}'s ${newOrExisting(
        getBuilderFromPot(rowOvr, pot)
      )} ${provProd(pot)}`;
    }

    if (lowerCaseAdvice.includes('withdrawal')) {
      return `Make a ${isSingle(rec)} withdrawal from ${owner}'s ${newOrExisting(
        getBuilderFromPot(rowOvr, pot)
      )} ${provProd(pot)}`;
    }

    if (lowerCaseAdvice.includes('modify')) {
      const modifyNote = rowOvr.note?.split('\n')[0];
      const modifyType = modifyNote !== undefined ? modifyChangesString[modifyNote] : '';
      return `Modify the ${modifyType} on ${owner}'s ${newOrExisting(
        getBuilderFromPot(rowOvr, pot)
      )} ${provProd(pot)}`;
    }

    if (lowerCaseAdvice.includes('action')) {
      return `Take action on ${owner}'s notes: ${noteSummary(rowOvr.note ?? '')}`;
    }

    return rec.advice ?? '';
  });

  return result;
};

export const ComprehensiveBuilderSummary: React.FC<{
  row: ComprehensiveBuilderSummaryRow;
}> = ({row}) => {
  const rowOvr = row as OVBuilders_ovrecommends;
  const rows = getComprehensiveBuilderSummary(rowOvr);
  return (
    <Box display="flex" flexDirection="column">
      {rows.map((line, i) => {
        return (
          <Box key={line} p={2} bgcolor={i % 2 === 0 ? 'whitesmoke' : 'gainsboro'}>
            <Typography>{line}</Typography>
          </Box>
        );
      })}
    </Box>
  );
};
